import { LoadingOutlined, PaperClipOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts"
import { DOCUMENT_SUPPORTED_FORMAT_MAP } from "@finway-group/shared/lib/consts/common"
import { DocumentTypeEnum, InboxInvoice, PriceIntervalEnum, RightEnum, UploadKindEnum } from "@finway-group/shared/lib/models"
import { Employee, GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { insertIf } from "@finway-group/shared/lib/utils"
import { Avatar, Button, Table, Tag, Tooltip } from "antd"
import { ColumnsType } from "antd/lib/table"
import moment from "moment"
import React, { Key, useState } from "react"
import { User as DefaultAssign } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import UserImage from "~Components/UserImage"
import { EmptyTableData } from "~Components/emptyTableData"
import { FilterModalLegacy } from "~Components/filter/FilterModalLegacy"
import { TrashIcon } from "~Components/icons/TrashIcon"
import { ErrorTable } from "~Components/layout/errorTable"
import Loading from "~Components/loading"
import InboxInvoiceModal from "~Components/modals/inboxInvoice/inboxInvoice.modal"
import OcrStatusBadge from "~Components/ocrStatusBadge"
import PriceLabel from "~Components/priceLabel"
import TableButtons from "~Components/tableButtons"
import UploadFileDraggerContent from "~Components/upload/UploadFileDraggerContent"
import UploadFilesDragger from "~Components/upload/UploadFilesDragger"
import { useSelectedInboxInvoices } from "~Shared/context/selectedExpenses.context"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { useExpenses } from "~Shared/hooks/expense.hooks"
import { useFetchTable, useTable, useTableFilterAndSearchLoading, useTableSearchString } from "~Shared/hooks/table.hooks"
import { useUserCreditors } from "~Shared/hooks/vendor.hooks"
import { AuthzService, ExpenseService, FileService, NotificationService, UserService } from "~Shared/services"
import DialogService from "~Shared/services/dialog.service"
import InboxInvoiceService from "~Shared/services/inboxInvoice.service"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { createInboxInvoice, deleteManyInboxInvoices, discardInboxInvoice, fetchDashboardInboxInvoicesCount } from "~Shared/store/actions/inboxInvoice/inboxInvoiceActions"
import { refetchTable, updateTableFilter, updateTableOnChange, updateTableSearch } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { getExpensesDocsNeeded } from "~Shared/utils/expense.utils"
import { isNotSet } from "~Shared/utils/helper.utils"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"
import { getSortOrderForColumn } from "~Shared/utils/table.utils"
import EInvoiceTag from "../../../../components/eInvoiceTag"
import { InboxInvoiceDropdown, InboxInvoiceOptionsEnum } from "./inboxInvoiceDropdown"

export const InboxInvoiceTab = () => {
    useFetchTable(TablesEnum.INBOX_INVOICE)
    const { expenses } = useExpenses(true)
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const employees = useEmployees()
    const [isUploading, setIsUploading] = useState(false)
    const tabLoading = useTableFilterAndSearchLoading(TablesEnum.INBOX_INVOICE)
    const loggedInEmployee = UserService.getLoggedInEmployeeProfile()
    const [isInvoiceInboxLoading, setIsInvoiceInboxLoading] = useStateIfMounted(false)
    const docsNeeded = getExpensesDocsNeeded(expenses)
    const [selectedInvoice, setSelectedInvoice] = useStateIfMounted<InboxInvoice | undefined>(undefined)
    const [isShowingModal, setIsShowingModal] = useState(false)
    const inboxInvoiceTable = useTable<InboxInvoice>(TablesEnum.INBOX_INVOICE)
    const searchString = useTableSearchString(TablesEnum.INBOX_INVOICE)
    const [selectedInboxInvoices, setSelectedInboxInvoices] = useSelectedInboxInvoices()
    const canWriteInboxInvoice = AuthzService.isRightGrantedForLoggedInUser(RightEnum.INBOX_INVOICE__ALL__WRITE)
    const isAllowedToCreateExpenses = AuthzService.isLoggedInUserAllowedToCreateExpenses()
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))

    const isFilterFilled = () => {
        const filterObject = inboxInvoiceTable.data.filter
        return Object.values(filterObject).some((val) => !isNotSet(val))
    }

    const signInvoiceUrl = async (id: string) => {
        try {
            const invoiceDocument = await InboxInvoiceService.fetchOneInboxInvoice(id)
            setSelectedInvoice(invoiceDocument)
        } catch (err) {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:error"), t("error:invoice.fetch_failed.message"))
        }
    }

    const openInvoicePreview = async (inboxInvoice: InboxInvoice) => {
        await signInvoiceUrl(inboxInvoice.id)
        setIsShowingModal(true)
    }

    const unassignInvoiceToUser = async (inboxInvoice: InboxInvoice, assignedTo: Employee) => {
        if (!(await DialogService.confirmUnassignInvoiceToUser(assignedTo.getFullName()))) {
            return
        }
        const newInvoice = new InboxInvoice({ ...inboxInvoice, assignedTo: null })

        try {
            const data = await InboxInvoiceService.updateOne(inboxInvoice.id, newInvoice)
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t("notification:invoice_inbox.unassign.title"),
                t("notification:invoice_inbox.unassign.message", { name: data.fileName, employee: assignedTo.getFullName() }),
                NotificationDurationSec.DEFAULT,
            )
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:invoice_inbox.unassign.title"))
        }
    }

    const onSelect =
        (inboxInvoice: InboxInvoice) =>
        async ({ key, domEvent }: { key: Key; domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> }) => {
            domEvent.stopPropagation()

            switch (Number(key)) {
                case InboxInvoiceOptionsEnum.REMOVE:
                    setIsInvoiceInboxLoading(true)
                    discardInboxInvoice(inboxInvoice)(dispatch)
                        .then(() =>
                            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:invoice_inbox.deleted.title"), t("notification:invoice_inbox.deleted.message")),
                        )
                        .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:invoice_inbox.delete.titlle")))
                        .finally(() => setIsInvoiceInboxLoading(false))
                    break
                case InboxInvoiceOptionsEnum.PREVIEW:
                    await openInvoicePreview(inboxInvoice)
                    break
                case InboxInvoiceOptionsEnum.MOVE:
                    domEvent.preventDefault()
                    break
                case InboxInvoiceOptionsEnum.DOWNLOAD:
                    FileService.downloadFileAsSignedUrl(inboxInvoice.url)
                    break
                case InboxInvoiceOptionsEnum.RETRY_OCR:
                    InboxInvoiceService.retryInboxInvoiceOcr(inboxInvoice.id)
                    break
                case InboxInvoiceOptionsEnum.UNASSIGN:
                    const employee = GetEmployeeById(employees, inboxInvoice.assignedTo)
                    if (employee) {
                        unassignInvoiceToUser(inboxInvoice, employee)
                    }
                    break
                default:
                    break
            }
        }

    const columns: ColumnsType<InboxInvoice> = [
        {
            title: t("info:inbox_invoice.file_name"),
            key: "fileName",
            align: "left",
            className: "pl-10",
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "fileName"),
            render: ({ fileName, duplicate }: InboxInvoice) => (
                <div data-testid="invoiceInboxFileNameColumn" className="flex flex-wrap">
                    <p className="truncate">{fileName}</p>
                    {duplicate.hash || duplicate.invoiceNumber ? <Tag className="ant-tag-red ml-10">{t("label:potential_duplicate")} </Tag> : <></>}
                </div>
            ),
        },
        {
            title: t("info:inbox_invoice.upload_date"),
            key: "uploadDate",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "uploadDate"),
            render: ({ uploadDate }: InboxInvoice) => <div>{moment(uploadDate).format("ll")}</div>,
        },
        {
            title: t("info:inbox_invoice.ocr"),
            key: "ocrStatus",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrStatus"),
            render: ({ ocrStatus, ocrResults }: InboxInvoice) =>
                ocrResults?.isEInvoice ? <EInvoiceTag className="inline-flex items-center" /> : <OcrStatusBadge ocrStatus={ocrStatus} />,
        },
        {
            title: t("info:inbox_invoice.vendor"),
            key: "ocrResults.vendor.name",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrResults.vendor.name"),
            render: ({ ocrResults }: InboxInvoice) => <div>{ocrResults?.vendor?.name || "n/a"} </div>,
        },
        {
            title: t("info:inbox_invoice.uploaded_by"),
            key: "uploadedByObject.firstName",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "uploadedByObject.firstName"),
            render: ({ uploadedBy }: InboxInvoice) => {
                const employee = GetEmployeeById(employees, uploadedBy)
                return (
                    <div>
                        {employee?.firstName} {employee?.lastName}
                    </div>
                )
            },
        },
        {
            width: 50,
            render: ({ assignedTo }: InboxInvoice) => {
                const employee = GetEmployeeById(employees, assignedTo)
                return (
                    <div className="flex items-center w-full justify-center">
                        {employee ? (
                            <UserImage user={employee} tooltipTitle={t("tooltips:inbox_invoice.assigned", { name: employee.getFullName() })} />
                        ) : (
                            <Tooltip title={t("tooltips:inbox_invoice.unassigned")} placement="top">
                                <Avatar size={24} style={{ backgroundColor: COLOR.gray[200] }} className="flex items-center justify-center">
                                    <DefaultAssign size={14} color={COLOR.text} />
                                </Avatar>
                            </Tooltip>
                        )}
                    </div>
                )
            },
        },
        {
            title: t("info:inbox_invoice.gross_amount"),
            key: "ocrResults.totalGrossPrice",
            width: 120,
            align: "right",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrResults.totalGrossPrice"),
            render: ({ ocrResults }: InboxInvoice) =>
                ocrResults?.totalGrossPrice && ocrResults?.currency ? (
                    <PriceLabel value={ocrResults.totalGrossPrice} currency={ocrResults.currency} interval={PriceIntervalEnum.ONE_TIME} />
                ) : (
                    <div>{"n/a"} </div>
                ),
        },
        ...insertIf(isAllowedToCreateExpenses, {
            title: t("info:inbox_invoice.action"),
            width: 120,
            align: "right" as const,
            className: "pr-10",
            render: (inboxInvoice: InboxInvoice) => <InboxInvoiceDropdown inboxInvoice={inboxInvoice} onSelect={onSelect} />,
        }),
    ]

    const doFileUpload = async (files: Array<File>) => {
        if (!FileService.checkFilesSizeAndHandleError(files)) {
            return
        }
        if (files.some((file) => !FileService.checkIsFileExtensionValidAndShowNotification(file, DOCUMENT_SUPPORTED_FORMAT_MAP[DocumentTypeEnum.INVOICE]))) {
            return
        }

        setIsUploading(true)
        try {
            const data = await FileService.uploadMultiple(files, UploadKindEnum.INBOX_INVOICE)
            for (const fileUploadData of data) {
                const createData = {
                    fileName: fileUploadData.originalname,
                    url: fileUploadData.url,
                    hash: fileUploadData.hash,
                    uploadDate: fileUploadData.createdAt,
                    uploadedBy: fileUploadData.createdBy,
                    duplicate: { hash: fileUploadData.duplicateData.isDuplicate },
                    fileId: fileUploadData.fileId,
                }
                if (await ExpenseService.checkForDuplicateInvoiceFile(fileUploadData.duplicateData)) {
                    createInboxInvoice(createData)(dispatch)
                }
            }
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:file.upload.title"))
        } finally {
            setIsUploading(false)
        }
    }

    const onDeleteSelectedInvoices = async () => {
        try {
            setIsInvoiceInboxLoading(true)
            const invoiceIds = selectedInboxInvoices.map((invoice) => invoice.id)
            await deleteManyInboxInvoices(invoiceIds)(dispatch)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsInvoiceInboxLoading(false)
        }
    }

    return (
        <>
            {tabLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="animation-appear">
                        <UploadFilesDragger doFileUpload={doFileUpload} documentType={DocumentTypeEnum.INVOICE} disabled={isUploading || !canWriteInboxInvoice}>
                            <UploadFileDraggerContent
                                label={{
                                    buttonLabel: t("action:add_invoice"),
                                    draggerDescription: t("action:or_drop_files"),
                                }}
                                isUploading={isUploading}
                                readOnly={!canWriteInboxInvoice}
                                isCompanyEmailForwardingAddressButtonVisible
                                // TODO: re-enable
                                // shouldShowCompanyEmailForwardingAddressButton
                            />
                        </UploadFilesDragger>
                    </div>
                    {(!!inboxInvoiceTable.data.totalDocs || inboxInvoiceTable.data.search || isFilterFilled()) && (
                        <TableButtons
                            table={TablesEnum.INBOX_INVOICE}
                            onSearch={async (searchString: string) => {
                                setIsInvoiceInboxLoading(true)
                                await dispatch(updateTableSearch(TablesEnum.INBOX_INVOICE, searchString))
                                await dispatch(fetchDashboardInboxInvoicesCount())
                                setIsInvoiceInboxLoading(false)
                            }}
                            injectFilterModal={(isVisible: boolean, onCancel: any) => (
                                <FilterModalLegacy
                                    table={TablesEnum.INBOX_INVOICE}
                                    options={{ userCreditors }}
                                    isVisible={isVisible}
                                    onCancel={onCancel}
                                    onSubmit={async (filterFormData: any) => {
                                        setIsInvoiceInboxLoading(true)
                                        await dispatch(updateTableFilter(TablesEnum.INBOX_INVOICE, filterFormData))
                                        await dispatch(fetchDashboardInboxInvoicesCount())
                                        setIsInvoiceInboxLoading(false)
                                    }}
                                />
                            )}
                            setIsShowingModal={setIsShowingModal}
                            additionalButtons={
                                <>
                                    <Button disabled={!isAllowedToCreateExpenses} onClick={() => setIsShowingModal(true)}>
                                        <PaperClipOutlined data-testid="attachToRequestInvoiceInbox" />
                                        {t("action:inbox_invoice.attach_to_request")}
                                    </Button>
                                    {canWriteInboxInvoice && (
                                        <Button onClick={onDeleteSelectedInvoices} disabled={!selectedInboxInvoices.length}>
                                            <TrashIcon data-testid="deleteInvoiceInbox" />
                                            {t("action:inbox_invoice.delete")}
                                        </Button>
                                    )}
                                </>
                            }
                        />
                    )}
                    {inboxInvoiceTable.error ? (
                        <ErrorTable onTableReload={() => dispatch(refetchTable(TablesEnum.INBOX_INVOICE))} isLoading={inboxInvoiceTable.isFetching} />
                    ) : inboxInvoiceTable.data.totalDocs === 0 && !inboxInvoiceTable.isFetching ? (
                        <EmptyTableData
                            isSearchOrFilterActive={!!searchString}
                            title={t("info:empty_state.inbox.title")}
                            message={t("info:empty_state.inbox.message")}
                            image="./icons/empty_inbox.svg"
                        />
                    ) : (
                        <div className="overflow-auto mb-24 mt-20 p'2 animation-appear">
                            <Table
                                id="table"
                                rowKey={(record) => record.id}
                                dataSource={inboxInvoiceTable.data.docs}
                                columns={columns}
                                onChange={updateTableOnChange(TablesEnum.INBOX_INVOICE, inboxInvoiceTable.data)}
                                loading={{
                                    spinning: inboxInvoiceTable.isFetching || isInvoiceInboxLoading,
                                    indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                                }}
                                rowSelection={{
                                    renderCell: (_value, _record, _index, originNode) => (
                                        <div onClick={(e) => e.stopPropagation()} className="bigger-hitbox-checkbox">
                                            {originNode}
                                        </div>
                                    ),
                                    selectedRowKeys: selectedInboxInvoices.map((record) => record.id),
                                    onChange: (_selectedRowKeys, selectedRows) => setSelectedInboxInvoices(selectedRows),
                                    onSelect: (record, _selected, _selectedRow) => setSelectedInboxInvoices([...selectedInboxInvoices, record]),
                                }}
                                pagination={{
                                    position: ["bottomRight"],
                                    showSizeChanger: true,
                                    current: inboxInvoiceTable.data.page,
                                    pageSize: inboxInvoiceTable.data.limit,
                                    hideOnSinglePage: false,
                                    total: inboxInvoiceTable.data.totalDocs,
                                    pageSizeOptions: ["5", "10", "20", "50", "100"],
                                }}
                                onRow={(record, _rowIndex) => ({
                                    onClick: (_event) => {
                                        openInvoicePreview(record)
                                    },
                                })}
                            />
                        </div>
                    )}
                    {isShowingModal && (
                        <InboxInvoiceModal
                            isShowing={isShowingModal}
                            onCancel={() => {
                                setIsShowingModal(false)
                                setSelectedInvoice(undefined)
                            }}
                            expenses={docsNeeded}
                            preSelectedInvoice={selectedInvoice}
                            shouldResetSelectedExpenseOnClose
                            shouldResetSelectedInboxOnClose
                            isOcrEnabled={loggedInEmployee.settings.enableOcr}
                            isOcrItemizationEnabled={loggedInEmployee.settings.enableOcrItemization}
                        />
                    )}
                </>
            )}
        </>
    )
}
